import React, {
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import * as d3 from 'd3';
import {
  calculateNewAbundances,
  getFuzzyCategory,
  getValueForAbundance,
} from 'constants/fcm';
import { augmentedMembership } from 'fcm/fcm';
import { getFilename, exportCsvData } from 'export/export_csv_data';
import { toDecimalPlace } from 'constants/basic_util';
import './AbundanceView.scss';
import { trophicLevels } from 'constants/nodes';
import AbundanceTable from 'components/abundanceView/AbundanceTable';
import { usePlayModeState } from 'state/PlayModeState';

const AbundanceView = forwardRef(({ base, matrix, pointOfComparison }, ref) => {
  const nodeResultsState = usePlayModeState(state => state.nodeResults);

  let actualBaseComparison = useMemo(() => {
    return pointOfComparison === 'results' ? matrix : base;
  }, [base, matrix, pointOfComparison]);

  let actualNextComparison = useMemo(() => {
    return matrix;
  }, [matrix]);

  const abundanceData = useMemo(() => {
    const matrixOldAbundances = Object.fromEntries(
      Object.values(actualBaseComparison.nodes).map(({ id, abundance }) => [
        id,
        abundance,
      ])
    );
    // If results, fake the comparison
    const matrixNewAbundances =
      pointOfComparison === 'results'
        ? nodeResultsState.reduce((acc, n) => {
            acc[n.id] = n.originalAbundanceResult;
            return acc;
          }, {})
        : calculateNewAbundances(base, matrixOldAbundances);

    // matrixNewFixed gates whether matrixPopulation divides by exaggeration.
    // Results: use originalFixed (set by addAbundancesToNodes from matrix.fixed)
    //   so overridden output nodes (n.fixed=true but originalAbundanceResult
    //   is not exaggerated) don't divide incorrectly.
    // Non-results: raw matrix nodes have n.fixed but not n.originalFixed.
    const matrixNewFixed =
      pointOfComparison === 'results'
        ? nodeResultsState.reduce((acc, n) => {
            acc[n.id] = n.originalFixed;
            return acc;
          }, {})
        : Object.values(base?.nodes).reduce((acc, n) => {
            acc[n.id] = n.fixed;
            return acc;
          }, {});

    const experimentOldAbundances = Object.fromEntries(
      Object.values(actualNextComparison.nodes).map(({ id, abundance }) => [
        id,
        abundance,
      ])
    );

    // If results, fake the comparison
    const experimentNewAbundances =
      pointOfComparison === 'results'
        ? nodeResultsState.reduce((acc, n) => {
            acc[n.id] = n.abundanceResult;
            return acc;
          }, {})
        : calculateNewAbundances(matrix, experimentOldAbundances);

    const experimentNewFixed =
      pointOfComparison === 'results'
        ? nodeResultsState.reduce((acc, n) => {
            acc[n.id] = n.fixed;
            return acc;
          }, {})
        : Object.values(matrix?.nodes).reduce((acc, n) => {
            acc[n.id] = n.fixed;
            return acc;
          }, {});

    let nodes = {};

    Object.values(actualBaseComparison.nodes).forEach(node => {
      const matrixAbundance = matrixNewAbundances?.[node.id];

      nodes[node.id] = {
        id: node.id,
        name: node.name,
        populationUnit: node.populationUnit,
        fixed: matrixNewFixed[node.id],
        trophicLevel: node.trophicLevel,
        matrixAbundance,
        matrixCategory: getFuzzyCategory(node, matrixAbundance),
        // TODO maybe here is where we adjust initial and after?
        matrixPopulation: getValueForAbundance(
          { ...node, fixed: matrixNewFixed[node.id] },
          matrixNewFixed[node.id]
            ? matrixNewAbundances[node.id] / (node?.abundanceExaggeration ?? 1)
            : matrixNewAbundances[node.id]
        ),
        matrixExaggeration: node?.abundanceExaggeration ?? 1,
      };
    });

    Object.values(actualNextComparison.nodes).forEach(node => {
      const experimentAbundance = experimentNewAbundances?.[node.id];

      const fallbackImage = trophicLevels.find(
        t => t?.name === node?.trophicLevel
      )?.icon;

      nodes[node.id] = {
        ...nodes[node.id],
        id: node.id,
        name: node.name,
        // TODO use trophic level image as backup
        imageUrl: node?.imageUrl,
        fallbackImageUrl: fallbackImage,
        populationUnit: node.populationUnit,
        fixed: experimentNewFixed[node.id],
        trophicLevel: node.trophicLevel,
        experimentAbundance,
        experimentCategory: getFuzzyCategory(node, experimentAbundance),
        experimentPopulation: getValueForAbundance(
          { ...node, fixed: experimentNewFixed[node.id] },
          experimentNewFixed[node.id]
            ? experimentNewAbundances[node.id] /
                (node?.abundanceExaggeration ?? 1)
            : experimentNewAbundances[node.id]
        ),
        experimentExaggeration: node?.abundanceExaggeration ?? 1,
      };
    });

    const expandCategoryNames = name => {
      if (name === augmentedMembership[2]) {
        return 'Moderate';
      } else {
        return name;
      }
    };

    const fuzzyCatChange = (matrixCategory, experimentCategory, change) => {
      if (change === 'node-added') {
        return 'Added';
      } else if (change === 'node-deleted') {
        return 'Removed';
      } else if ((matrixCategory != null) & (experimentCategory != null)) {
        let expandedMatrixCat = expandCategoryNames(matrixCategory);
        let expandedExperimentCat = expandCategoryNames(experimentCategory);

        expandedMatrixCat =
          expandedMatrixCat.charAt(0).toUpperCase() +
          expandedMatrixCat.slice(1).toLowerCase();

        expandedExperimentCat =
          expandedExperimentCat.charAt(0).toUpperCase() +
          expandedExperimentCat.slice(1).toLowerCase();

        return !(matrixCategory === experimentCategory)
          ? expandedMatrixCat + '→' + expandedExperimentCat
          : 'No Change';
      } else {
        return matrixCategory != null ? 'Node Removed' : ' Node Added';
      }
    };

    const determineChange = node => {
      const isFixed = pointOfComparison === 'results' && node?.fixed;

      if (node.matrixPopulation === node.experimentPopulation) {
        return {
          change: 'no-change',
          changeDisplay: fuzzyCatChange(
            node.matrixCategory,
            node.experimentCategory,
            'no-change'
          ),
        };
      } else if (node.matrixAbundance < node.experimentAbundance) {
        return {
          change: isFixed ? 'fixed' : 'increase',
          changeDisplay: fuzzyCatChange(
            node.matrixCategory,
            node.experimentCategory,
            'increase'
          ),
        };
      } else if (node.matrixAbundance > node.experimentAbundance) {
        return {
          change: isFixed ? 'fixed' : 'decrease',
          changeDisplay: fuzzyCatChange(
            node.matrixCategory,
            node.experimentCategory,
            'decrease'
          ),
        };
      } else if (node.matrixPopulation == null) {
        return {
          change: 'node-added',
          changeDisplay: fuzzyCatChange(
            node.matrixCategory,
            node.experimentCategory,
            'node-added'
          ),
          matrixPopulation: 0,
          matrixAbundance: 0,
        };
      } else if (node.experimentPopulation == null) {
        return {
          change: 'node-deleted',
          changeDisplay: fuzzyCatChange(
            node.matrixCategory,
            node.experimentCategory,
            'node-deleted'
          ),
          experimentPopulation: 0,
          experimentAbundance: 0,
        };
      } else {
        return { change: 'no-change' };
      }
    };

    return Object.values(nodes).map(node => {
      if (node?.name === 'Sun') {
        console.log(node);
      }

      return {
        ...node,
        ...determineChange(node),
      };
    });
  }, [
    base,
    matrix,
    nodeResultsState,
    pointOfComparison,
    actualBaseComparison,
    actualNextComparison,
  ]);

  const sortedAbundanceData = useMemo(() => {
    const getSortProperties = node => {
      const absoluteAbundanceDiff = Math.abs(
        node.matrixAbundance - node.experimentAbundance
      );
      const absolutePopulationDiff = Math.abs(
        node.matrixPopulation - node.experimentPopulation
      );

      const populationChanged =
        absolutePopulationDiff != null && absolutePopulationDiff !== 0;

      let percentChange =
        ((node.experimentPopulation - node.matrixPopulation) /
          node.matrixPopulation) *
        100;

      if (isNaN(percentChange)) {
        percentChange = 0;
      }

      percentChange = parseFloat(percentChange.toFixed(2));

      percentChange = `${
        percentChange > 0 ? `+${percentChange}` : percentChange
      }%`;

      return {
        percentChange,
        absoluteAbundanceDiff,
        absolutePopulationDiff,
        populationChanged,
      };
    };

    const sorted = Object.values(abundanceData).map(node => ({
      ...node,
      ...getSortProperties(node),
    }));

    const sortBy = 'absoluteAbundanceDiff';

    return sorted
      .sort((a, b) => d3.ascending(a['name'], b['name']))
      .sort((a, b) => d3.descending(a[sortBy], b[sortBy]))
      .sort((a, b) =>
        d3.descending(a['populationChanged'], b['populationChanged'])
      );
  }, [abundanceData]);

  const downloadData = useCallback(() => {
    const dataDownload = sortedAbundanceData.map(node => {
      return {
        Name: node.name,
        Units: node.populationUnit,
        'Original absolute abundance': toDecimalPlace(node.matrixPopulation, 4),
        'Modified absolute abundance': toDecimalPlace(
          node.experimentPopulation,
          4
        ),
        'Original relative abundance': toDecimalPlace(node.matrixAbundance, 4),
        'Modified relative abundance': toDecimalPlace(
          node.experimentAbundance,
          4
        ),
        'Fuzzy change': node.changeDisplay,
      };
    });

    const filename = getFilename(matrix, 'abundance_changes');

    const columnOrder = [
      'Name',
      'Units',
      'Original absolute abundance',
      'Modified absolute abundance',
      'Original relative abundance',
      'Modified relative abundance',
      'Fuzzy change',
    ];

    exportCsvData(dataDownload, filename, columnOrder);
  }, [matrix, sortedAbundanceData]);

  useImperativeHandle(
    ref,
    () => ({
      downloadData,
    }),
    [downloadData]
  );

  return (
    <div id="AbundanceView" className="AbundanceView">
      <div className="AbundanceView-content">
        <AbundanceTable
          nodes={sortedAbundanceData}
          experimentName={matrix?.name}
          pointOfComparison={pointOfComparison}
        />
      </div>
    </div>
  );
});

export default AbundanceView;
